import React from "react";
import { OrderHistoryProps, PeriodDetails } from "../types/dashboard";
import OrderSummary from "./OrderSummary";
import { useUser } from "src/shared/context/UserContext";
import { getReceivedLabels } from "../utils/dashboardLabels";

const OrderHistory: React.FC<OrderHistoryProps> = ({ dashboardData = {}, businessType }) => {
  const { userConfigration } = useUser();

  const toBoolean = (value: any) => value === true || value === "true";
  const parcelFeeEnabled = toBoolean(userConfigration?.parcelFeeEnabled);
  const tipsEnabled = toBoolean(userConfigration?.tipsEnabled);

  const showParcelFee = businessType === "Restaurant" && parcelFeeEnabled;
  const showTips = businessType === "Restaurant" && tipsEnabled;
  const isRestaurant = businessType === "Restaurant";

  const periodicDetails = dashboardData?.periodic_order_details || {};
  const n = (v: any) => Number(v) || 0;

  const getReceivedLabelsForTimeRange = (timeRange: string) => getReceivedLabels(timeRange as keyof typeof getReceivedLabels);

  // One place for the received split, used by every period
  const buildPeriod = (data: Partial<PeriodDetails> = {}, orderKey: keyof PeriodDetails, ledgerKey: keyof PeriodDetails) => {
    const outstandingReceived = n(data?.outstandingLedgerReceived) + n(data?.outstandingOrderReceived);
    const apiTotalReceived = n(data?.totalReceived);

    // Only the received amount belonging to this period
    const ownOrderReceived = n(data?.[orderKey]) + n(data?.[ledgerKey]);

    // Restaurant: totalReceived already includes outstanding receipts
    const todaysOrderReceived = isRestaurant ? Math.max(apiTotalReceived - outstandingReceived, 0) : ownOrderReceived;

    // This is the amount displayed in the Received section
    const totalReceived = isRestaurant ? apiTotalReceived : ownOrderReceived + outstandingReceived;

    const totalSales = n(data.totalSales);
    const sales = n(data?.netSales);

    const orderDiscount = n(data?.totalDiscount || 0);
    const ledgerDiscount = n(data?.ledgerDiscount || 0);
    const totalDiscount = orderDiscount + ledgerDiscount;

    // IMPORTANT:
    // Outstanding = Sales - current period received
    const outstanding = Math.max(sales - ownOrderReceived, 0);

    return {
      orders: n(data?.totalOrders),
      amount: totalSales,
      received: totalReceived,
      cancelRefund: n(data?.cancelRefund),
      ledgerRefund: n(data?.ledgerRefund),
      todaysOrderReceived,
      outstandingLedgerReceived: outstandingReceived,
      outstanding,
      DeliveryFee: n(data?.totalDeliveryFee),
      // Discount: n(data?.combinedDiscount),
      Discount: totalDiscount, // Keep this for backward compatibility
      orderDiscount, // New field
      ledgerDiscount, // New field

      ParcelFee: showParcelFee ? n(data?.totalParcelFee) : 0,

      tips: showTips ? n(data?.totalTip) : 0,
    };
  };

  const today = buildPeriod(periodicDetails?.today, "todaysOrderReceived", "todaysLedgerReceived");
  const yesterday = buildPeriod(periodicDetails?.yesterday, "yesterdayOrderReceived", "yesterdayLedgerReceived");
  const week = buildPeriod(periodicDetails?.weekly, "weeklyOrderReceived", "weeklyLedgerReceived");
  const month = buildPeriod(periodicDetails?.monthly, "monthlyOrderReceived", "monthlyLedgerReceived");

  return (
    <div className="w-full my-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-5 items-stretch">
        <OrderSummary
          title="Today's Orders"
          timeRange="today"
          {...today}
          businessType={businessType}
          showParcelFee={showParcelFee}
          showTips={showTips}
          receivedLabels={getReceivedLabelsForTimeRange("today")}
        />
        <OrderSummary
          title="Yesterday's Orders"
          timeRange="yesterday"
          {...yesterday}
          businessType={businessType}
          showParcelFee={showParcelFee}
          showTips={showTips}
          receivedLabels={getReceivedLabelsForTimeRange("yesterday")}
        />
        <OrderSummary
          title="Last 7 Days Orders"
          timeRange="week"
          {...week}
          businessType={businessType}
          showParcelFee={showParcelFee}
          showTips={showTips}
          receivedLabels={getReceivedLabelsForTimeRange("week")}
        />
        <OrderSummary
          title="This Month's Orders"
          timeRange="month"
          {...month}
          businessType={businessType}
          showParcelFee={showParcelFee}
          showTips={showTips}
          receivedLabels={getReceivedLabelsForTimeRange("month")}
        />
      </div>
    </div>
  );
};

export default OrderHistory;
